// Cheap reflection layer for baked surfaces that carry extras.reflect (contract section 2).
//
// One cube map is captured once at the manifest's probe position (or built from env_url), with mipmaps.
// Each reflective MeshBasicMaterial gets a patched fragment: box-projected reflection vector (room-sized
// parallax correction, so reflections of the windows sit in the right place on the floor), a Schlick
// fresnel term and a mip level picked from extras.roughness. The result is mixed over the baked colour.
// Cost: one textureLod per fragment on reflective surfaces only; one shader program for all of them.

import {
  Box3,
  CubeCamera,
  HalfFloatType,
  LinearMipmapLinearFilter,
  Mesh,
  MeshBasicMaterial,
  SRGBColorSpace,
  UnsignedByteType,
  Vector3,
  WebGLCubeRenderTarget,
  type Material,
  type Scene,
  type Texture,
  type WebGLRenderer,
} from 'three';

export interface ReflectionSettings {
  strength: number;
  roughness: number;
}

/** Uniforms shared by every reflective material (one object, referenced by all programs). */
const shared = {
  uBoxMin: { value: new Vector3(-1e3, -1e3, -1e3) },
  uBoxMax: { value: new Vector3(1e3, 1e3, 1e3) },
  uProbePos: { value: new Vector3() },
  uMaxLod: { value: 8 },
};

const VERT_PARS = /* glsl */ `
varying vec3 vRWorldPos;
varying vec3 vRWorldNormal;
`;
const VERT_MAIN = /* glsl */ `
#include <envmap_vertex>
#ifdef USE_ENVMAP
vRWorldPos = worldPosition.xyz;
vRWorldNormal = normalize( mat3( modelMatrix ) * objectNormal );
#endif
`;
const FRAG_PARS = /* glsl */ `
varying vec3 vRWorldPos;
varying vec3 vRWorldNormal;
uniform vec3 uBoxMin;
uniform vec3 uBoxMax;
uniform vec3 uProbePos;
uniform float uMaxLod;
uniform float uStrength;
uniform float uRough;
`;
const FRAG_MAIN = /* glsl */ `
#ifdef USE_ENVMAP
{
  vec3 N = normalize( vRWorldNormal );
  vec3 V = normalize( vRWorldPos - cameraPosition );
  if ( dot( N, V ) > 0.0 ) N = -N;
  vec3 R = reflect( V, N );
  // box projection: intersect the reflection ray with the room box, look up the probe toward the hit
  vec3 tA = ( uBoxMax - vRWorldPos ) / R;
  vec3 tB = ( uBoxMin - vRWorldPos ) / R;
  vec3 tF = max( tA, tB );
  float t = min( min( tF.x, tF.y ), tF.z );
  vec3 dir = vRWorldPos + R * max( t, 0.0 ) - uProbePos;
  float NoV = clamp( dot( N, -V ), 0.0, 1.0 );
  float f = pow( 1.0 - NoV, 5.0 );
  float F = 0.12 + 0.88 * f * ( 1.0 - 0.6 * uRough );
  float lod = clamp( uRough * uMaxLod * 1.15, 0.0, uMaxLod );
  vec3 env = textureLod( envMap, envMapRotation * dir, lod ).rgb;
  outgoingLight = mix( outgoingLight, env, clamp( F * uStrength, 0.0, 1.0 ) );
}
#endif
`;

/** Patch a MeshBasicMaterial (from KHR_materials_unlit) to add the reflection layer. */
export function makeReflective(material: MeshBasicMaterial, s: ReflectionSettings): void {
  const uStrength = { value: s.strength };
  const uRough = { value: s.roughness };
  material.userData.reflection = { uStrength, uRough };
  material.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, shared, { uStrength, uRough });
    shader.vertexShader = VERT_PARS + shader.vertexShader.replace('#include <envmap_vertex>', VERT_MAIN);
    shader.fragmentShader = FRAG_PARS + shader.fragmentShader.replace('#include <envmap_fragment>', FRAG_MAIN);
  };
  material.customProgramCacheKey = () => 'lr3d-reflect-v1';
  material.needsUpdate = true;
}

export function readReflection(mesh: Mesh): ReflectionSettings | null {
  const mat = mesh.material as Material;
  const src = (mesh.userData.reflect !== undefined ? mesh.userData : mat.userData) as Record<string, unknown>;
  const strength = Number(src.reflect);
  if (!(strength > 0)) return null;
  const roughness = Number(src.roughness ?? mat.userData.roughness ?? 0.2);
  return { strength: Math.min(1, strength), roughness: Number.isFinite(roughness) ? Math.min(1, Math.max(0, roughness)) : 0.2 };
}

export interface ProbeResult {
  texture: Texture;
  bytes: number;
  dispose(): void;
}

/**
 * Capture the scene into a mipmapped cube map at `position`. Reflective materials must not have their
 * envMap yet (so they render as plain baked colour into the probe).
 */
export function captureProbe(renderer: WebGLRenderer, scene: Scene, position: Vector3, size: number, hdr: boolean): ProbeResult {
  const rt = new WebGLCubeRenderTarget(size, {
    generateMipmaps: true,
    minFilter: LinearMipmapLinearFilter,
    type: hdr ? HalfFloatType : UnsignedByteType,
    colorSpace: hdr ? undefined : SRGBColorSpace,
  });
  const cam = new CubeCamera(0.02, 100, rt);
  cam.position.copy(position);
  scene.add(cam);
  cam.updateMatrixWorld(true);
  cam.update(renderer, scene);
  scene.remove(cam);
  const bpp = hdr ? 8 : 4;
  return { texture: rt.texture, bytes: Math.round(size * size * 6 * bpp * 1.333), dispose: () => rt.dispose() };
}

/** Build the cube from an equirectangular .hdr (manifest reflection.env_url). */
export async function loadEnvProbe(renderer: WebGLRenderer, url: string, size: number): Promise<ProbeResult> {
  const { HDRLoader } = await import('three/examples/jsm/loaders/HDRLoader.js'); // only with an env_url
  const tex = await new HDRLoader().loadAsync(url);
  const rt = new WebGLCubeRenderTarget(size, { generateMipmaps: true, minFilter: LinearMipmapLinearFilter, type: HalfFloatType });
  rt.fromEquirectangularTexture(renderer, tex);
  tex.dispose();
  return { texture: rt.texture, bytes: Math.round(size * size * 6 * 8 * 1.333), dispose: () => rt.dispose() };
}

export function setProbeGeometry(box: Box3, probe: Vector3, cubeSize: number): void {
  shared.uBoxMin.value.copy(box.min);
  shared.uBoxMax.value.copy(box.max);
  shared.uProbePos.value.copy(probe);
  shared.uMaxLod.value = Math.log2(cubeSize);
}

export function applyProbe(materials: Iterable<MeshBasicMaterial>, texture: Texture): void {
  for (const m of materials) {
    m.envMap = texture;
    m.needsUpdate = true;
  }
}
